import React from "react"
import { Box } from "@chakra-ui/react"

import BrokerGuidancePanel, { useBrokerGuidanceColors } from "./BrokerGuidancePanel"
import {
  ChecklistRow,
  GuidanceAccordionBody,
  GuidanceBlock,
  GuidanceIntro,
} from "./ibkrGuidanceShared"

export interface LiquidBrokersStatementImportGuidanceProps {
  compact?: boolean
  /** When false, omit Tessera account / re-import notes (demo has no account chrome). */
  showAccountNotes?: boolean
}

/**
 * Liquid Brokers statement export help (Liquid Charts Pro Account History → Statement).
 * @see https://liquidbrokers.kb.help/downloading-statements-on-liquid-charts-pro/
 */
const LiquidBrokersStatementImportGuidance = ({
  compact = false,
  showAccountNotes = true,
}: LiquidBrokersStatementImportGuidanceProps): React.ReactElement => {
  const colors = useBrokerGuidanceColors()

  return (
    <BrokerGuidancePanel
      title="How to export your Liquid Brokers statement"
      compact={compact}
      collapsible
    >
      <GuidanceAccordionBody>
        <GuidanceIntro color={colors.bodyColor} className="!mb-0">
          Tessera expects the tab-separated Account History{" "}
          <Box as="span" fontWeight="semibold">
            Statement
          </Box>{" "}
          from Liquid Charts Pro, with{" "}
          <Box as="span" fontWeight="semibold">
            Open / Close / Financing
          </Box>{" "}
          rows, not a custom spreadsheet.
        </GuidanceIntro>

        <GuidanceBlock title="Export steps">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            In Liquid Charts Pro, open the three-line menu (top left) →{" "}
            <Box as="span" fontWeight="semibold">
              Account History
            </Box>
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Click{" "}
            <Box as="span" fontWeight="semibold">
              Statement
            </Box>
            , then Statement again to show the download button
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Download the statement and upload it here
          </ChecklistRow>
        </GuidanceBlock>

        <GuidanceBlock title="Required columns">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            <Box as="span" fontWeight="semibold">
              Time
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              Reference
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              Type
            </Box>{" "}
            (Open / Close / Financing),{" "}
            <Box as="span" fontWeight="semibold">
              Description
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              Market
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              Lots
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              Price
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              Amount
            </Box>
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Optional when present:{" "}
            <Box as="span" fontWeight="semibold">
              Stop Loss
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              Take Profit
            </Box>
          </ChecklistRow>
        </GuidanceBlock>

        <GuidanceBlock title="Timezone">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Set statement timezone to the IANA zone that matches times in the file (e.g.
            Europe/London). A wrong zone shifts every trade timestamp.
          </ChecklistRow>
        </GuidanceBlock>

        {showAccountNotes ? (
          <GuidanceBlock title="Account in Tessera">
            <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
              Enter your Liquid{" "}
              <Box as="span" fontWeight="semibold">
                account code
              </Box>{" "}
              (the account identifier you trade under). Re-importing updates that same account.
            </ChecklistRow>
          </GuidanceBlock>
        ) : null}
      </GuidanceAccordionBody>
    </BrokerGuidancePanel>
  )
}

export default LiquidBrokersStatementImportGuidance
